// Haptic feedback utility for mobile devices using Capacitor Haptics
import { Haptics, ImpactStyle, NotificationType } from '@capacitor/haptics';
import { Capacitor } from '@capacitor/core';

export interface HapticFeedbackOptions {
  type?: 'light' | 'medium' | 'heavy' | 'selection' | 'impact' | 'notification';
  duration?: number;
}

// Check if device supports haptic feedback (native platform)
export const isHapticSupported = (): boolean => {
  return Capacitor.isNativePlatform();
};

// Check if vibration API is available as fallback
const isVibrationSupported = (): boolean => {
  return typeof navigator !== 'undefined' && 'vibrate' in navigator;
};

// Trigger haptic feedback using Capacitor Haptics
export const triggerHapticFeedback = async (options: HapticFeedbackOptions = {}): Promise<void> => {
  const { type = 'light', duration = 50 } = options;

  try {
    // Use Capacitor Haptics on native platforms
    if (isHapticSupported()) {
      switch (type) {
        case 'light':
          await Haptics.impact({ style: ImpactStyle.Light });
          break;
        case 'medium':
          await Haptics.impact({ style: ImpactStyle.Medium });
          break;
        case 'heavy':
          await Haptics.impact({ style: ImpactStyle.Heavy });
          break;
        case 'selection':
          await Haptics.selectionChanged();
          break;
        case 'notification':
          await Haptics.notification({ type: NotificationType.Success });
          break;
        case 'impact':
          await Haptics.impact({ style: ImpactStyle.Medium });
          break;
        default:
          await Haptics.impact({ style: ImpactStyle.Light });
      }
      return;
    }

    // Fallback to vibration API for web/PWA
    if (isVibrationSupported()) {
      let pattern: number[];

      switch (type) {
        case 'light':
          pattern = [duration];
          break;
        case 'medium':
          pattern = [Math.round(duration * 1.5)];
          break;
        case 'heavy':
          pattern = [duration * 2];
          break;
        case 'selection':
          pattern = [25];
          break;
        case 'impact':
          pattern = [duration, 50, duration];
          break;
        case 'notification':
          pattern = [100, 50, 100];
          break;
        default:
          pattern = [duration];
      }

      navigator.vibrate(pattern);
    }
  } catch (error) {
    // Silently fail if haptic feedback is not supported
    console.debug('Haptic feedback not supported:', error);
  }
};

// Specific haptic feedback functions for rosary interactions
export const hapticFeedback = {
  // Light tap for bead transitions
  beadTransition: () => triggerHapticFeedback({ type: 'light' }),

  // Light tap for normal bead interaction (alias)
  beadTap: () => triggerHapticFeedback({ type: 'light' }),

  // Selection feedback for step advance
  stepAdvance: () => triggerHapticFeedback({ type: 'selection' }),

  // Medium feedback for prayer step completion
  stepComplete: () => triggerHapticFeedback({ type: 'medium' }),

  // Heavy feedback for decade completion
  decadeComplete: () => triggerHapticFeedback({ type: 'heavy' }),

  // Selection feedback for bead selection
  beadSelect: () => triggerHapticFeedback({ type: 'selection' }),

  // Medium impact for mystery transitions
  mysteryTransition: () => triggerHapticFeedback({ type: 'medium' }),

  // Notification for prayer completion
  prayerComplete: () => triggerHapticFeedback({ type: 'notification' }),

  // Impact feedback for important transitions
  transition: () => triggerHapticFeedback({ type: 'impact' }),

  // Heavy impact for special moments
  specialMoment: () => triggerHapticFeedback({ type: 'heavy' }),
};

// Hook to get haptic feedback preference from settings
export const useHapticPreference = (): boolean => {
  try {
    const preference = localStorage.getItem('hapticFeedbackEnabled');
    return preference !== 'false'; // Default to true unless explicitly disabled
  } catch {
    return true; // Default to enabled
  }
};

// Save haptic feedback preference
export const setHapticPreference = (enabled: boolean): void => {
  try {
    localStorage.setItem('hapticFeedbackEnabled', enabled.toString());
  } catch (error) {
    console.warn('Could not save haptic preference:', error);
  }
};